import React from 'react';
import {
  Building2, Code2, Clock, Shield, Network, FileCheck,
  Server, Layers, CheckCircle2, ExternalLink, Users, Linkedin
} from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const About = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [techRef, techVisible] = useScrollAnimation({ threshold: 0.2 });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.2 });
  const [teamRef, teamVisible] = useScrollAnimation({ threshold: 0.2 });

  const techStats = [
    { value: "15,000+", label: "Lines of Production Code", icon: Code2 },
    { value: "6", label: "Native Protocol Implementations", icon: Network },
    { value: "11", label: "Months of Development", icon: Clock },
    { value: "50+", label: "Source Files", icon: Layers }
  ];

  const systemComponents = [
    { name: "Decision Engine", desc: "~2ns amortized", included: true },
    { name: "Risk Manager", desc: "12+ controls", included: true },
    { name: "Order Manager (OMS)", desc: "FIX-compliant", included: true },
    { name: "Position Manager", desc: "Real-time P&L", included: true },
    { name: "Feed Handlers", desc: "ITCH, FAST, WS", included: true },
    { name: "Order Encoders", desc: "OUCH, FIX, SBE", included: true },
    { name: "Compliance", desc: "SEBI, MiFID II", included: true },
    { name: "Monitoring", desc: "Prometheus metrics", included: true }
  ];

  const founders = [
    {
      name: "Santosh T",
      role: "Founder & Chief Architect",
      experience: "14+ years",
      description: "Enterprise technology leader with deep expertise in security architecture, network engineering, and technical leadership at global technology companies.",
      expertise: ["Security Architecture", "Network Engineering", "Technical Leadership", "Enterprise Systems"],
      color: "cyan"
    },
    {
      name: "Suresh Sharma",
      role: "Strategic Advisor & Co-Founder",
      experience: "30+ years",
      description: "Cybersecurity veteran and former CISO at PayU and Motilal Oswal. Brings deep expertise in financial services security and regulatory compliance.",
      expertise: ["Cybersecurity", "Financial Services", "CISO Experience", "Regulatory Compliance"],
      color: "purple"
    }
  ];

  return (
    <section id="about" className="py-24 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-6">
            <Building2 className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-purple-400">About ASTRA</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 tracking-tight">
            The Technology & <span className="text-gradient">Team</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            A complete HFT infrastructure system built by enterprise technology veterans.
            Deep security expertise, distilled into production-ready code.
          </p>
        </div>

        {/* Tech Stats */}
        <div
          ref={statsRef}
          className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 transition-all duration-700 ${
            statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {techStats.map((stat, index) => (
            <div
              key={index}
              className="p-6 rounded-2xl glass-card text-center group hover:scale-105 transition-transform duration-300"
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                <stat.icon className="w-6 h-6 text-cyan-400" />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
          {/* What's Included */}
          <div
            ref={techRef}
            className={`transition-all duration-700 ${
              techVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="p-6 sm:p-8 rounded-2xl glass-intense border border-white/10 h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                  <Server className="w-5 h-5 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Complete System</h3>
              </div>

              <p className="text-muted-foreground mb-6 text-sm">
                ASTRA is a complete HFT infrastructure system—not just a decision engine.
                Built from scratch with institutional-grade components.
              </p>

              <div className="grid grid-cols-2 gap-3">
                {systemComponents.map((comp, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-3 rounded-lg bg-white/5 border border-white/5"
                  >
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-sm font-medium text-white">{comp.name}</div>
                      <div className="text-xs text-muted-foreground">{comp.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20">
                <p className="text-sm text-muted-foreground">
                  <span className="text-white font-medium">Zero external dependencies</span> in the hot path.
                  All protocol codecs, risk management, and compliance built from scratch.
                </p>
              </div>
            </div>
          </div>

          {/* Why Compliance is Built-In */}
          <div
            className={`transition-all duration-700 delay-200 ${
              techVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="p-6 sm:p-8 rounded-2xl glass-intense border border-white/10 h-full">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                  <FileCheck className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Compliance is Architectural</h3>
              </div>

              <p className="text-muted-foreground mb-6 text-sm">
                Built by a team with 44+ combined years implementing enterprise security and compliance.
                SEBI, MiFID II, and SEC requirements aren't afterthoughts—they're design decisions.
              </p>

              <div className="space-y-3 mb-6">
                <div className="text-sm font-medium text-white mb-2">Built-In Compliance:</div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "SEBI OTR Monitoring", desc: "Order-to-Trade Ratio" },
                    { label: "Kill Switch", desc: "Hardware-bound halt" },
                    { label: "Audit Trail", desc: "5-year retention" },
                    { label: "Anti-Manipulation", desc: "5 detection algorithms" },
                    { label: "MiFID II RTS 6/25", desc: "European compliance" },
                    { label: "SEC Rule 17a-4", desc: "WORM logging" }
                  ].map((item, index) => (
                    <div key={index} className="p-2 rounded-lg bg-white/5 border border-white/5">
                      <div className="text-xs font-medium text-cyan-400">{item.label}</div>
                      <div className="text-xs text-muted-foreground">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {["ISO 27001", "SOC 2", "PCI-DSS", "SEBI", "MiFID II"].map((cert, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Founding Team Section */}
        <div
          ref={teamRef}
          className={`max-w-6xl mx-auto mb-16 transition-all duration-700 ${
            teamVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
              <Users className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-medium text-cyan-400">Founding Team</span>
            </div>
            <h3 className="text-2xl font-bold text-white">
              Enterprise Veterans Building for HFT
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {founders.map((founder, index) => (
              <div
                key={index}
                className={`p-6 sm:p-8 rounded-2xl glass-intense border ${
                  founder.color === 'cyan' ? 'border-cyan-500/20' : 'border-purple-500/20'
                }`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold ${
                    founder.color === 'cyan'
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                  }`}>
                    {founder.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-white">{founder.name}</h4>
                    <p className={`text-sm font-medium ${
                      founder.color === 'cyan' ? 'text-cyan-400' : 'text-purple-400'
                    }`}>
                      {founder.role}
                    </p>
                    <p className="text-xs text-muted-foreground">{founder.experience} experience</p>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  {founder.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {founder.expertise.map((skill, idx) => (
                    <span
                      key={idx}
                      className={`px-2 py-1 rounded-md text-xs font-medium ${
                        founder.color === 'cyan'
                          ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                          : 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                      }`}
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Team Credibility Statement */}
          <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-cyan-500/10 border border-blue-500/20 text-center">
            <p className="text-muted-foreground">
              <span className="text-white font-medium">Why this matters:</span> Our enterprise security background
              is why compliance is <span className="text-cyan-400 font-medium">architectural, not bolted on</span>.
              We've implemented regulatory frameworks at scale—now we're building them into HFT infrastructure.
            </p>
          </div>
        </div>

        {/* Bottom Trust Statement */}
        <div className="max-w-4xl mx-auto">
          <div className="p-8 rounded-2xl glass-card text-center">
            <Shield className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-3">
              Don't Trust Marketing. Verify Yourself.
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              We built ASTRA to be verifiable. Every performance claim is reproducible.
              Our architecture documentation is available for serious inquiries.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#contact"
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:shadow-lg hover:shadow-cyan-500/25 transition-all"
              >
                Get in Touch
              </a>
              <a
                href="#faq"
                className="px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium hover:bg-white/10 transition-colors"
              >
                Read FAQ
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
